import {Component, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {UtilsService} from '@services/utils/utils.service';
import {ArrayOperationService} from '@services/administration/array-operation/array-operation.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {NotificationService} from '@services/notification/notification.service';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {MachineGroupService} from '@services/administration/machine-group/machine-group.service';
import {MachineGroup} from '@models/adminstration/machine-group/machine-group';
import {User} from '@models/user/user';
import {Role} from '@models/user/role';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';
import {
  MachineGroupEditComponent
} from '@components/administration/machine-group/machine-group-edit/machine-group-edit.component';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {ElasticSearchResult} from '@models/elastic-search/response/elastic-search-result';
import {ElasticSearchSortValue} from '@models/elastic-search/request/elastic-search-sort-value';
import {catchError, switchMap, takeUntil} from 'rxjs/operators';
import {forkJoin, Observable, of, Subject, throwError} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {ModalService} from '@services/modal/modal.service';
import {ITEMS_PER_PAGE_OPTIONS} from '@constants/pagination/pagination.constant';
import {PaginateControlSettings} from '@models/paginate-control-settings';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';

@Component({
    selector: 'scitis-admin-machine-group',
    templateUrl: './machine-group.component.html',
    styleUrls: ['./machine-group.component.scss'],
    standalone: false
})
export class MachineGroupComponent implements OnInit, OnDestroy {

  @ViewChildren(TableHeaderSortableDirective)
  sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();

  public isSuperAdminRole: boolean = false;
  public isAdminOrHigher: boolean = false;
  public isDistributorRole: boolean = false;
  public machinegroupList: MachineGroup[] = new Array<MachineGroup>();
  public rootMachinegroupId: number = -1;
  public searchTerm: string = '';
  public machineryGroupCount: number = 0;
  public loggedUser: User = new User();
  public itemsPerPageOptions: number[] = ITEMS_PER_PAGE_OPTIONS;
  public paginateSettings: PaginateControlSettings = new PaginateControlSettings({
    id: 'machineGroupPaginationId',
  });

  private sortValue = new ElasticSearchSortValue();
  private CURRENT_PAGE_NAME = 'Machinery group';
  private searchSubject$: Subject<string> = new Subject<string>();
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private isAdminRole: boolean = false;
  private isTechAdminRole: boolean = false;
  private selectedOrganizationId = -1;
  private whiteListedMachineryGroups: number[] | null = null;

  constructor(
      private loadingIndicator: LoadingIndicatorService,
      private firebaseAuthenticationService: FirebaseAuthenticationService,
      private translation: TranslateService,
      private notification: NotificationService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private modalService: ModalService,
      private machineGroupService: MachineGroupService,
      private utilsService: UtilsService,
      private arrayOperationService: ArrayOperationService,
      private router: Router,
      private activatedRoute: ActivatedRoute,
      private elasticSearchService: ElasticSearchService,
      private menuNotificationService: MenuNotificationService
  ) {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(({search}) => {
      this.searchTerm = search || '';
      this.searchSubject$.next(this.searchTerm);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.setMachineGroups();
    this.searchSubject$
    .pipe(takeUntil(this.destroy$))
    .subscribe(value => {
      this.searchTerm = value;
      this.paginateSettings = {
        ...this.paginateSettings,
        currentPage: 1,
      };
      this.setMachineGroups();
    });
  }

  public onSearch({target}): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {search: target.value},
      queryParamsHandling: 'merge',
    });
  }

  public onAddMachineGroup() {
    const modalRef = this.modalService.open(MachineGroupEditComponent, {size: 'lg'});
    modalRef.componentInstance.isSuperAdminRole = this.isSuperAdminRole;
    modalRef.componentInstance.isAdminOrHigher = this.isAdminOrHigher;
    modalRef.componentInstance.isDistributorRole = this.isDistributorRole;
    modalRef.componentInstance.rootMachinegroupId = this.rootMachinegroupId;
    modalRef.componentInstance.loggedUser = this.loggedUser;
    modalRef.componentInstance.machinegroup = new MachineGroup();
    modalRef.result.then((result) => {
      console.log(`Closed with: ${result}`);
      this.loadingIndicator.show(this.translation.instant('Adding machinery group'));
      this.machineGroupService.addMachineryGroup(result).subscribe({
        next: (success) => {
          this.machinegroupList = [...this.machinegroupList, success];
          this.machineryGroupCount++;
          this.paginateSettings = {
            ...this.paginateSettings,
            totalItems: this.machineryGroupCount,
          };
          this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, '', success.name, ''));
          const message = this.translation.instant('Machinery group') + ' ' + success.name + ' ' + this.translation.instant('added') + '.';
          this.notification.success(message, this.CURRENT_PAGE_NAME, false);
          this.loadingIndicator.hide();
        }, error: (error) => {
          this.notification.error(this.translation.instant('Error adding machinery group') + ': ' + result.name, error, this.CURRENT_PAGE_NAME, true);
          this.loadingIndicator.hide();
        }
      });
    }, (reason) => {
      console.log(`Closed with reason: ${reason}`);
    });
  }

  onColumnSort({column, direction}: SortEvent) {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.sortValue = this.tableHeaderSortableService.getSortValue({column, direction});
    this.setMachineGroups();
  }

  onDeleteMachinegroupSuccess(machinegroup: MachineGroup) {
    this.machineryGroupCount--;
    this.paginateSettings = {
      ...this.paginateSettings,
      totalItems: this.machineryGroupCount,
    };
    const index = this.machinegroupList.indexOf(machinegroup);
    if (index >= 0) {
      this.machinegroupList.splice(index, 1);
    }
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.paginateSettings = {
      ...this.paginateSettings,
      itemsPerPage: itemsPerPage,
      currentPage: 1,
    };
    this.setMachineGroups();
  }

  onLoadPageNumber(pageNumber: number): void {
    this.paginateSettings = {
      ...this.paginateSettings,
      currentPage: pageNumber,
    };
    this.setMachineGroups();
  }

  private setMachineGroups(): void {
    if (this.elasticSearchService.isInvalidSearchTerm(this.searchTerm)) {
      this.machinegroupList = [];
      this.machineryGroupCount = 0;
      this.paginateSettings = {...this.paginateSettings, totalItems: this.machineryGroupCount};
      return;
    }

    this.machinegroupList = new Array<MachineGroup>();
    this.machineryGroupCount = 0;
    this.loadingIndicator.show(this.translation.instant('Loading'));
    this.firebaseAuthenticationService.getLoggedUserDetails()
    .pipe(
        takeUntil(this.destroy$),
        switchMap((user: User) => {
          if (user.parentId) {
            this.loggedUser = user;
            const userRole = new Role(user);
            this.rootMachinegroupId = userRole.isSuperAdminRole ? -1 : user.machineGroup;
            this.selectedOrganizationId = userRole.isSuperAdminRole ? -1 : user.parentId;
            this.whiteListedMachineryGroups = userRole.isSuperAdminRole ? null : user.getWhiteListedMachineryGroups(true);
            this.isSuperAdminRole = userRole.isSuperAdminRole;
            this.isAdminRole = userRole.isAdminRole;
            this.isTechAdminRole = userRole.isTechAdminRole;
            this.isDistributorRole = userRole.isDistributorRole;
            this.isAdminOrHigher = this.utilsService.isAdminOrHigher(this.isAdminRole, this.isTechAdminRole, this.isSuperAdminRole);
          }

          const countObserver = this.searchTerm ? of(null) : this.setMachineryGroupsCountForUser();
          return forkJoin([
            this.elasticSearchService.listMachineryGroups(this.searchTerm, this.sortValue, this.paginateSettings.currentPage, this.paginateSettings.itemsPerPage, this.rootMachinegroupId, this.whiteListedMachineryGroups),
            countObserver
          ]);
        })).subscribe(([listMachineryGroups, count]) => {
      if (listMachineryGroups?.result?.data) {
        const result: ElasticSearchResult = listMachineryGroups.result;
        this.machinegroupList = result.data;
        if (this.searchTerm) {
          this.machineryGroupCount = result?.totalHits || 0;
          this.paginateSettings = {...this.paginateSettings, totalItems: this.machineryGroupCount};
        }
      }
      if (count) {
        this.machineryGroupCount = count?.result?.totalHits || 0;
        this.paginateSettings = {...this.paginateSettings, totalItems: this.machineryGroupCount};
      }
      this.loadingIndicator.hide();
    }, error => {
      this.machineryGroupCount = 0;
      const message = this.translation.instant('Error getting machinery group list.');
      this.loadingIndicator.hide();
      this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
    });
  }

  private setMachineryGroupsCountForUser(): Observable<any> {
    return this.elasticSearchService.countMachineryGroups(this.rootMachinegroupId, this.whiteListedMachineryGroups)
    .pipe(catchError((error) => {
      this.machineryGroupCount = 0;
      console.error(`${this.CURRENT_PAGE_NAME}. Error getting machinery groups count.`, JSON.stringify(error));
      return throwError(error);
    }));
  }
}
